import { useMemo } from "react";
import { useSuperAgentsList } from "./useSuperAgentsList.js";
import { usePlcsList } from "./usePlcsList.js";
import { useAgentsList } from "./useAgentsList.js";

// The real server-side hard cap every Worker Line Admin list endpoint
// enforces (Math.Clamp(pageSize, 1, 100) in WorkerLineAdminService) - never
// requested higher. Composing the org chart from these three already-real,
// already-scope-isolated list endpoints (Prompt 131/132) rather than the
// thinner GET /api/backoffice/hierarchy/tree endpoint is a deliberate
// Prompt 137 choice: the list endpoints already carry status/plcCount/
// agentCount/workerCount/inviteCount/applicationCount/activeAssignmentCount
// per row (WorkerLineSuperAgentListItemDto/WorkerLinePlcListItemDto/
// WorkerLineAgentListItemDto), which the tree endpoint's own DTOs
// (HierarchyDtos.cs) do not return at all - using them means Prompt 137
// needs no backend change to show real counts/status on every node.
const ORG_CHART_PAGE_SIZE = 100;

function buildOrgTree(superAgentItems, plcItems, agentItems) {
  const plcsBySuperAgent = new Map();
  for (const plc of plcItems) {
    const key = plc.superAgentOrganizationId;
    if (!plcsBySuperAgent.has(key)) {
      plcsBySuperAgent.set(key, []);
    }
    plcsBySuperAgent.get(key).push(plc);
  }

  const agentsByPlc = new Map();
  for (const agent of agentItems) {
    const key = agent.plcOrganizationId;
    if (!agentsByPlc.has(key)) {
      agentsByPlc.set(key, []);
    }
    agentsByPlc.get(key).push(agent);
  }

  // P156: the side belongs to the LINE, not to each row, and the three list
  // endpoints are fetched independently. Rather than trust each row's own
  // copy - which would let a PLC render as one side while its Super Agent
  // renders as the other - the Super Agent's side is pushed down the branch.
  // The Super Agent is the only place a side is authored, so it is the only
  // honest source for the nodes beneath it.
  return superAgentItems.map((superAgent) => ({
    ...superAgent,
    plcs: (plcsBySuperAgent.get(superAgent.superAgentOrganizationId) ?? []).map((plc) => ({
      ...plc,
      organizationSide: superAgent.organizationSide,
      agents: (agentsByPlc.get(plc.plcOrganizationId) ?? []).map((agent) => ({
        ...agent,
        organizationSide: superAgent.organizationSide,
      })),
    })),
  }));
}

/**
 * Composes the Worker Line Org Chart (Prompt 137) from three already-real,
 * already-scope-isolated Worker Line Admin list endpoints
 * (GET .../worker-line/super-agents|plcs|agents, each unfiltered except for
 * page/pageSize so every row within the caller's own visible scope comes
 * back) instead of the thinner hierarchy/tree endpoint - see this file's own
 * top note for why. Grouping (PLCs under their Super Agent, Agents under
 * their PLC) happens client-side from the real superAgentOrganizationId/
 * plcOrganizationId columns every row already carries, never invented.
 *
 * `totals` exposes the real, globally-accurate counts each list response's
 * own `totalCount` already provides (a real SQL COUNT(*) across the whole
 * scope, not just the current page) for the Super Agent/PLC/Agent KPI
 * tiles. The Workers KPI is a sum of each fetched Super Agent's own real
 * `workerCount` - exact as long as every Super Agent fit within one
 * ORG_CHART_PAGE_SIZE page (`workerCountPartial` is true otherwise, so the
 * caller can label it honestly instead of presenting a partial sum as the
 * platform-wide total). Active/Inactive counts are computed the same way
 * over the fetched Agents (`agentsStatusPartial` mirrors the same caveat).
 */
export function useWorkerLineOrgChart() {
  const superAgentsFilters = useMemo(() => ({ page: 1, pageSize: ORG_CHART_PAGE_SIZE }), []);
  const plcsFilters = useMemo(() => ({ page: 1, pageSize: ORG_CHART_PAGE_SIZE }), []);
  const agentsFilters = useMemo(() => ({ page: 1, pageSize: ORG_CHART_PAGE_SIZE }), []);

  const superAgents = useSuperAgentsList(superAgentsFilters);
  const plcs = usePlcsList(plcsFilters);
  const agents = useAgentsList(agentsFilters);

  const allReady = superAgents.data !== null && plcs.data !== null && agents.data !== null;
  const anyErrorWithNoData =
    (superAgents.status === "error" && superAgents.data === null) ||
    (plcs.status === "error" && plcs.data === null) ||
    (agents.status === "error" && agents.data === null);

  const status = anyErrorWithNoData ? "error" : allReady ? "ready" : "loading";
  const error = anyErrorWithNoData ? (superAgents.error ?? plcs.error ?? agents.error) : null;

  const tree = useMemo(() => {
    if (!allReady) {
      return null;
    }
    return buildOrgTree(superAgents.data.items, plcs.data.items, agents.data.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allReady already gates on all three .data being non-null
  }, [allReady, superAgents.data, plcs.data, agents.data]);

  const totals = useMemo(() => {
    if (!allReady) {
      return null;
    }
    const superAgentTotal = superAgents.data.totalCount;
    const plcTotal = plcs.data.totalCount;
    const agentTotal = agents.data.totalCount;
    const workerCount = superAgents.data.items.reduce((sum, item) => sum + (item.workerCount ?? 0), 0);
    const agentsActive = agents.data.items.filter((item) => item.status === "Active").length;
    const agentsInactive = agents.data.items.filter((item) => item.status === "Inactive").length;
    return {
      superAgentTotal,
      plcTotal,
      agentTotal,
      workerCount,
      workerCountPartial: superAgents.data.items.length < superAgentTotal,
      agentsActive,
      agentsInactive,
      agentsStatusPartial: agents.data.items.length < agentTotal,
      // P163: the list endpoints clamp pageSize to 100, so once a side holds
      // more than that the canvas draws a prefix of the structure while these
      // KPI tiles keep showing the real totals. Expose how many of each are
      // actually drawn so the page can say so, instead of letting the chart
      // silently contradict the numbers printed directly above it.
      plcShown: plcs.data.items.length,
      agentsShown: agents.data.items.length,
      treePartial:
        plcs.data.items.length < plcTotal ||
        agents.data.items.length < agentTotal ||
        superAgents.data.items.length < superAgentTotal,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allReady already gates on all three .data being non-null
  }, [allReady, superAgents.data, plcs.data, agents.data]);

  function refetch() {
    superAgents.refetch();
    plcs.refetch();
    agents.refetch();
  }

  return { status, error, tree, totals, refetch };
}
